import type { AiProfile } from "@/types/ai";
import type { KarmaProfile } from "@/types/karmaProfile";

/**
 * Generate natural, personality-driven microcopy based on KARMA Core tone
 */

export function getInputPlaceholder(profile: AiProfile, karmaProfile?: KarmaProfile): string {
  const warmth = karmaProfile?.tone.warmth ?? 50;
  const formality = karmaProfile?.tone.formality ?? 50;
  const humour = karmaProfile?.tone.humour ?? 30;
  
  const name = profile.name;
  
  // Warm and friendly
  if (warmth > 70 && formality < 40) {
    const options = [
      `What's on your mind?`,
      `What can I help with?`,
      `What would you like to know?`,
      `Tell me what you're thinking...`,
    ];
    return options[Math.floor(Math.random() * options.length)];
  }
  
  // Professional but approachable
  if (formality > 60 && warmth > 50) {
    const options = [
      `How can I assist you today?`,
      `What would you like to discuss?`,
      `I'm here to help. What's your question?`,
    ];
    return options[Math.floor(Math.random() * options.length)];
  }
  
  // Very formal
  if (formality > 75) {
    return `Please state your inquiry.`;
  }
  
  // Playful with humour
  if (humour > 60 && warmth > 60) {
    const options = [
      `What's up?`,
      `What can we explore together?`,
      `What's on your mind?`,
    ];
    return options[Math.floor(Math.random() * options.length)];
  }
  
  // Default: natural and conversational
  return `What would you like to know?`;
}

export function getEmptyStateMessage(profile: AiProfile, karmaProfile?: KarmaProfile): {
  title: string;
  subtitle: string;
} {
  const warmth = karmaProfile?.tone.warmth ?? 50;
  const formality = karmaProfile?.tone.formality ?? 50;
  
  // Warm and friendly
  if (warmth > 70 && formality < 40) {
    return {
      title: "Ready when you are",
      subtitle: "Ask me anything, or just start a conversation.",
    };
  }
  
  // Professional
  if (formality > 60) {
    return {
      title: "How can I assist you?",
      subtitle: "I'm here to help with your questions and tasks.",
    };
  }
  
  // Default: natural
  return {
    title: "Let's get started",
    subtitle: "Ask me anything, or share what's on your mind.",
  };
}

export function getLoadingMessage(profile: AiProfile, karmaProfile?: KarmaProfile): string {
  const warmth = karmaProfile?.tone.warmth ?? 50;
  const humour = karmaProfile?.tone.humour ?? 30;
  
  // Playful
  if (humour > 60 && warmth > 60) {
    const options = [
      "Thinking...",
      "Working on it...",
      "Let me figure that out...",
    ];
    return options[Math.floor(Math.random() * options.length)];
  }
  
  // Warm
  if (warmth > 70) {
    return "Just a moment...";
  }
  
  // Default
  return "Processing...";
}

export function getErrorMessage(profile: AiProfile, karmaProfile?: KarmaProfile): string {
  const warmth = karmaProfile?.tone.warmth ?? 50;
  const formality = karmaProfile?.tone.formality ?? 50;
  
  // Warm and friendly
  if (warmth > 70 && formality < 40) {
    return "Hmm, I ran into an issue. Could you try again?";
  }
  
  // Professional
  if (formality > 60) {
    return "I encountered an error. Please try again.";
  }
  
  // Default
  return "Something went wrong. Please try again.";
}

